import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const LocationSubmissionsContext = createContext();

export const useLocationSubmissions = () => {
  const context = useContext(LocationSubmissionsContext);
  if (!context) {
    throw new Error('useLocationSubmissions must be used within a LocationSubmissionsProvider');
  }
  return context;
};

export const LocationSubmissionsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for location submissions endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getSubmissionsBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/location-submissions/api/v1/location-submissions`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/location-submissions/api/v1/location-submissions`;
    } else {
      throw new Error('Invalid login type for location submissions access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access location submissions');
    }
  }, [loginType]);

  // Get all submissions with pagination and filters
  const getSubmissions = useCallback(async (options = {}) => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.submissionStatus) queryParams.append('submission_status', options.submissionStatus);
      if (options.locationCategory) queryParams.append('location_category', options.locationCategory);
      if (options.locationCity) queryParams.append('location_city', options.locationCity);
      if (options.userCredentials !== undefined) queryParams.append('user_credentials', options.userCredentials);
      if (options.submissionDateFrom) queryParams.append('submission_date_from', options.submissionDateFrom);
      if (options.submissionDateTo) queryParams.append('submission_date_to', options.submissionDateTo);
      if (options.userEmail) queryParams.append('user_email', options.userEmail);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getSubmissionsBaseUrl();
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch submissions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get Added Locations
  const getAddedLocations = useCallback(async (options = {}) => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);

      const baseUrl = getSubmissionsBaseUrl();
      const fullUrl = `${baseUrl}/added-locations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      console.log('[getAddedLocations] Fetching from URL:', fullUrl);

      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);

      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch added locations';
      console.error('[getAddedLocations] Error:', errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Delete Location and Reject Submission
  const deleteLocationAndReject = useCallback(async (locationId, submissionId, notes = null) => {
    validateAccess();

    if (!locationId || !submissionId) {
      throw new Error('Both Location ID and Submission ID are required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const body = {
        location_id: locationId,
        submission_id: submissionId,
        ...(notes && { notes })
      };

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/added-locations/delete-and-reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete location and reject submission';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Bulk Get Locations by Submission IDs
  const bulkGetLocationsBySubmissionIds = useCallback(async (submissionIds) => {
    validateAccess();

    if (!submissionIds || !Array.isArray(submissionIds) || submissionIds.length === 0) {
      throw new Error('At least one submission ID is required');
    }

    if (submissionIds.length > 500) {
      throw new Error('Maximum 500 submission IDs can be queried at once');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/locations/bulk-by-submission-ids`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ submission_ids: submissionIds }),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk fetch locations by submission IDs';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get submission by ID
  const getSubmissionById = useCallback(async (submissionId) => {
    validateAccess();

    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/submission/${encodeURIComponent(submissionId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get submission by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get submissions by user email
  const getSubmissionsByUserEmail = useCallback(async (userEmail, options = {}) => {
    validateAccess();

    if (!userEmail) {
      throw new Error('User email is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);

      const baseUrl = getSubmissionsBaseUrl();
      const url = `${baseUrl}/user/${encodeURIComponent(userEmail)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;

      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get submissions by user email';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get submission statistics
  const getSubmissionStats = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get submission statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get unique statuses
  const getUniqueStatuses = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/statuses`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get unique statuses';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get unique categories
  const getUniqueCategories = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/categories`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get unique categories';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get unique cities
  const getUniqueCities = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/cities`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get unique cities';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Approve submission
  const approveSubmission = useCallback(async (submissionId) => {
    validateAccess();

    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/submission/${encodeURIComponent(submissionId)}/approve`,
        {
          method: 'POST',
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to approve submission';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Reject submission
  const rejectSubmission = useCallback(async (submissionId, notes = null) => {
    validateAccess();

    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const body = notes ? JSON.stringify({ notes }) : JSON.stringify({});

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/submission/${encodeURIComponent(submissionId)}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: body,
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to reject submission';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // --- NEW: Mark Submission as Duplicate ---
  const markSubmissionAsDuplicate = useCallback(async (submissionId, notes = null) => {
    validateAccess();

    if (!submissionId) {
      throw new Error('Submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const body = notes ? JSON.stringify({ notes }) : JSON.stringify({});

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/submission/${encodeURIComponent(submissionId)}/duplicate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: body,
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to mark submission as duplicate';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Update submission status
  const updateSubmissionStatus = useCallback(async (submissionId, status, notes = null) => {
    validateAccess();

    if (!submissionId) {
      throw new Error('Submission ID is required');
    }
    if (!status) {
      throw new Error('Status is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const body = { status };
      if (notes) body.notes = notes;

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/submission/${encodeURIComponent(submissionId)}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update submission status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // CREATE — admin curated location, credentials forced to "prlocal" server-side
  const createLocation = useCallback(async (locationData) => {
    validateAccess();
    if (!locationData.location_type || !locationData.location_name) {
      throw new Error('location_type and location_name are required');
    }

    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(
        `${getSubmissionsBaseUrl()}/locations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(locationData),
        }
      );
      return await handleApiResponse(response);
    } catch (err) {
      setError(err.message); throw err;
    } finally { setLoading(false); }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // READ one
  const getLocationById = useCallback(async (locationId) => {
    validateAccess();
    if (!locationId) throw new Error('locationId is required');

    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        `${getSubmissionsBaseUrl()}/locations/${encodeURIComponent(locationId)}`
      );
      return await handleApiResponse(response);
    } catch (err) {
      setError(err.message); throw err;
    } finally { setLoading(false); }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // UPDATE — partial. credentials and submission_id are not editable.
  const updateLocation = useCallback(async (locationId, updates) => {
    validateAccess();
    if (!locationId) throw new Error('locationId is required');

    // Strip protected fields client-side too, just so a typo doesn't waste a round-trip
    const { credentials, submission_id, location_id, ...allowed } = updates;

    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        `${getSubmissionsBaseUrl()}/locations/${encodeURIComponent(locationId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(allowed),
        }
      );
      return await handleApiResponse(response);
    } catch (err) {
      setError(err.message); throw err;
    } finally { setLoading(false); }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // DELETE — standalone, works for admin-created and submission-derived rows
  const deleteLocation = useCallback(async (locationId) => {
    validateAccess();
    if (!locationId) throw new Error('locationId is required');

    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        `${getSubmissionsBaseUrl()}/locations/${encodeURIComponent(locationId)}`,
        { method: 'DELETE' }
      );
      return await handleApiResponse(response);
    } catch (err) {
      setError(err.message); throw err;
    } finally { setLoading(false); }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Update submission notes
  const updateSubmissionNotes = useCallback(async (submissionId, notes) => {
    validateAccess();

    if (!submissionId) {
      throw new Error('Submission ID is required');
    }
    if (!notes) {
      throw new Error('Notes are required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/submission/${encodeURIComponent(submissionId)}/notes`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notes }),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update submission notes';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Bulk approve submissions
  const bulkApproveSubmissions = useCallback(async (submissionIds) => {
    validateAccess();

    if (!submissionIds || !Array.isArray(submissionIds) || submissionIds.length === 0) {
      throw new Error('At least one submission ID is required');
    }

    if (submissionIds.length > 100) {
      throw new Error('Maximum 100 submissions can be approved at once');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/bulk/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ submission_ids: submissionIds }),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk approve submissions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Bulk reject submissions
  const bulkRejectSubmissions = useCallback(async (submissionIds, notes = null) => {
    validateAccess();

    if (!submissionIds || !Array.isArray(submissionIds) || submissionIds.length === 0) {
      throw new Error('At least one submission ID is required');
    }

    if (submissionIds.length > 100) {
      throw new Error('Maximum 100 submissions can be rejected at once');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const body = { submission_ids: submissionIds };
      if (notes) body.notes = notes;

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/bulk/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk reject submissions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Bulk Approve and Fetch Created Locations
  const bulkApproveAndFetchLocations = useCallback(async (submissionIds) => {
    validateAccess();

    if (!submissionIds || !Array.isArray(submissionIds) || submissionIds.length === 0) {
      throw new Error('At least one submission ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const approvalResult = await bulkApproveSubmissions(submissionIds);

      const approvedIds = approvalResult.results
        .filter(result => result.status === 'Approved')
        .map(result => result.submission_id);

      if (approvedIds.length === 0) {
        return {
          approvalResult,
          locations: [],
          message: 'No submissions were successfully approved',
        };
      }

      const locationsResult = await bulkGetLocationsBySubmissionIds(approvedIds);

      return {
        approvalResult,
        locations: locationsResult.locations,
        approvedCount: approvedIds.length,
        locationCount: locationsResult.count,
        message: `Successfully approved ${approvedIds.length} submission(s) and fetched ${locationsResult.count} location(s)`,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk approve and fetch locations';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [validateAccess, bulkApproveSubmissions, bulkGetLocationsBySubmissionIds]);

  // Get pending submissions
  const getPendingSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, submissionStatus: 'Pending' });
  }, [getSubmissions, validateAccess]);

  // Get approved submissions
  const getApprovedSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, submissionStatus: 'Approved' });
  }, [getSubmissions, validateAccess]);

  // Get rejected submissions
  const getRejectedSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, submissionStatus: 'Rejected' });
  }, [getSubmissions, validateAccess]);

  // --- NEW: Get Duplicate Submissions ---
  const getDuplicateSubmissions = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, submissionStatus: 'Duplicate' });
  }, [getSubmissions, validateAccess]);

  // Get submissions by status
  const getSubmissionsByStatus = useCallback(async (status, options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, submissionStatus: status });
  }, [getSubmissions, validateAccess]);

  // Get submissions by category
  const getSubmissionsByCategory = useCallback(async (category, options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, locationCategory: category });
  }, [getSubmissions, validateAccess]);

  // Get submissions by city
  const getSubmissionsByCity = useCallback(async (city, options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, locationCity: city });
  }, [getSubmissions, validateAccess]);

  // Get submissions with credentials
  const getSubmissionsWithCredentials = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, userCredentials: true });
  }, [getSubmissions, validateAccess]);

  // Get submissions without credentials
  const getSubmissionsWithoutCredentials = useCallback(async (options = {}) => {
    validateAccess();
    return await getSubmissions({ ...options, userCredentials: false });
  }, [getSubmissions, validateAccess]);

  // Get submissions by date range
  const getSubmissionsByDateRange = useCallback(async (dateFrom, dateTo, options = {}) => {
    validateAccess();
    return await getSubmissions({
      ...options,
      submissionDateFrom: dateFrom,
      submissionDateTo: dateTo
    });
  }, [getSubmissions, validateAccess]);

  // Search submissions
  const searchSubmissions = useCallback(async (searchTerm, options = {}) => {
    validateAccess();

    if (!searchTerm || searchTerm.length < 2) {
      throw new Error('Search term must be at least 2 characters');
    }

    return await getSubmissions({ ...options, search: searchTerm });
  }, [getSubmissions, validateAccess]);

  // Export submissions data
  const exportSubmissionsData = useCallback(async (filters = {}) => {
    validateAccess();

    const exportOptions = {
      ...filters,
      limit: 10000,
      offset: 0,
    };

    const result = await getSubmissions(exportOptions);

    const exportData = result.submissions.map(submission => ({
      'Submission ID': submission.submission_id,
      'User Email': submission.user_email || '',
      'Location Category': submission.location_category || '',
      'Location Name': submission.location_name || '',
      'Location City': submission.location_city || '',
      'Submission Date': submission.submission_date ? new Date(submission.submission_date).toLocaleDateString() : '',
      'Status': submission.submission_status || '',
      'User Credentials': submission.user_credentials ? 'Yes' : 'No',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      filename: `location_submissions_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getSubmissions, validateAccess]);

  // Get submission summary
  const getSubmissionSummary = useCallback(async () => {
    validateAccess();

    try {
      const [stats, recentSubmissions] = await Promise.all([
        getSubmissionStats(),
        getSubmissions({ limit: 10 })
      ]);

      return {
        totalSubmissions: stats.stats.total_submissions,
        withCredentials: stats.stats.with_credentials,
        withoutCredentials: stats.stats.without_credentials,
        submissionsWithImages: stats.stats.submissions_with_images,
        averageImagesPerSubmission: stats.stats.average_images_per_submission,
        submissionsByStatus: stats.stats.submissions_by_status,
        submissionsByCategory: stats.stats.submissions_by_category,
        submissionsByCity: stats.stats.submissions_by_city,
        recentSubmissions: recentSubmissions.submissions,
        accessLevel: loginType,
        hasSubmissions: stats.stats.total_submissions > 0,
        pendingCount: stats.stats.submissions_by_status?.Pending || 0,
        approvedCount: stats.stats.submissions_by_status?.Approved || 0,
        rejectedCount: stats.stats.submissions_by_status?.Rejected || 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get submission summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getSubmissionStats, getSubmissions, validateAccess, loginType]);

  // Health check
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master'
        ? `${API_BASE_URL}/master/location-submissions/health`
        : `${API_BASE_URL}/admin/location-submissions/health`;

      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateSubmissionFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.submissionDateFrom && filters.submissionDateTo &&
      new Date(filters.submissionDateFrom) > new Date(filters.submissionDateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Format submission data
  const formatSubmissionData = useCallback((submission) => {
    const getStatusColor = (status) => {
      const colors = {
        'Pending': 'orange',
        'Approved': 'green',
        'Rejected': 'red',
        'Duplicate': 'gray', // Added color for Duplicate
        'Under Review': 'blue',
      };
      return colors[status] || 'gray';
    };

    const getCategoryIcon = (category) => {
      const icons = {
        'Beach': '🏖️',
        'Trail': '🥾',
        'Park': '🌳',
        'Waterfall': '💧',
        'Cave': '🕳️',
        'Forest': '🌲',
        'Lake': '🏞️',
      };
      return icons[category] || '📍';
    };

    return {
      ...submission,
      statusColor: getStatusColor(submission.submission_status),
      categoryIcon: getCategoryIcon(submission.location_category),
      credentialsText: submission.user_credentials ? 'With Credentials' : 'Anonymous',
      credentialsColor: submission.user_credentials ? 'green' : 'gray',
      formattedSubmissionDate: submission.submission_date
        ? new Date(submission.submission_date).toLocaleDateString()
        : 'N/A',
      formattedSubmissionTime: submission.submission_time || 'N/A',
      hasImages: !!(submission.image_url_1 || submission.image_url_2 || submission.image_url_3),
      imageCount: [submission.image_url_1, submission.image_url_2, submission.image_url_3]
        .filter(Boolean).length,
      hasCoordinates: !!(submission.latitude && submission.longitude),
      coordinatesText: submission.latitude && submission.longitude
        ? `${submission.latitude.toFixed(6)}, ${submission.longitude.toFixed(6)}`
        : 'No coordinates',
      isPending: submission.submission_status === 'Pending',
      isApproved: submission.submission_status === 'Approved',
      isRejected: submission.submission_status === 'Rejected',
      isDuplicate: submission.submission_status === 'Duplicate', // Added Duplicate flag
    };
  }, []);

  // Get approval candidates (submissions ready for approval)
  const getApprovalCandidates = useCallback(async (options = {}) => {
    validateAccess();

    try {
      const pendingSubmissions = await getPendingSubmissions(options);

      // Filter for complete submissions with required fields
      const candidates = pendingSubmissions.submissions.filter(submission =>
        submission.location_name &&
        submission.location_category &&
        submission.location_city
      );

      return {
        ...pendingSubmissions,
        submissions: candidates,
        count: candidates.length,
        originalCount: pendingSubmissions.count,
        readyForApproval: candidates.length,
        needsReview: pendingSubmissions.count - candidates.length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get approval candidates';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getPendingSubmissions, validateAccess]);

  // Get endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master'
        ? `${API_BASE_URL}/master/location-submissions/api/v1/location-submissions`
        : `${API_BASE_URL}/admin/location-submissions/api/v1/location-submissions`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,

    // Core Actions
    getSubmissions,
    getSubmissionById,
    getSubmissionsByUserEmail,
    getSubmissionStats,
    getUniqueStatuses,
    getUniqueCategories,
    getUniqueCities,

    // Location Actions
    getAddedLocations,
    deleteLocationAndReject,
    bulkGetLocationsBySubmissionIds,
    bulkApproveAndFetchLocations,

    // Approval Actions
    approveSubmission,
    rejectSubmission,
    markSubmissionAsDuplicate, // NEW
    updateSubmissionStatus,
    updateSubmissionNotes,
    bulkApproveSubmissions,
    bulkRejectSubmissions,

    // Filter Actions
    getPendingSubmissions,
    getApprovedSubmissions,
    getRejectedSubmissions,
    getDuplicateSubmissions, // NEW
    getSubmissionsByStatus,
    getSubmissionsByCategory,
    getSubmissionsByCity,
    getSubmissionsWithCredentials,
    getSubmissionsWithoutCredentials,
    getSubmissionsByDateRange,
    searchSubmissions,

    // Utility Actions
    exportSubmissionsData,
    checkHealth,
    getSubmissionSummary,
    getApprovalCandidates,

    //new Crud functions
    createLocation,
    getLocationById,
    updateLocation,
    deleteLocation,

    // Helpers
    clearError,
    validateSubmissionFilters,
    formatSubmissionData,
    getEndpointInfo,

    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasSubmissionsAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <LocationSubmissionsContext.Provider value={value}>
      {children}
    </LocationSubmissionsContext.Provider>
  );
};